import { PAYMENT_TYPES, STRIPE_PAYMENT_INTENT_ID_PATTERN, type SubmitBasketData } from '@koala/sdk';

/**
 * Stripe 3D Secure / SCA verification at checkout — TEAM-14331.
 *
 * When a card's issuer asks the diner to verify, coo-api answers basket submit with
 * `402 payment_verification_required` and the challenge in `error_data` (TEAM-14330; contract in
 * coo-api `docs/STRIPE_PAYMENT_VERIFICATION.md`). The storefront runs the challenge with Stripe.js
 * and resubmits the **same** basket with the verified intent in place of the spent card token.
 *
 * `client_secret` authorises confirming or cancelling the diner's payment intent, so it lives only
 * in the saga that runs the challenge: never in redux, a log line or Sentry. `redactVerificationSecret`
 * is the guard for the one place the raw response is still logged.
 */

const PAYMENT_VERIFICATION_REQUIRED_STATUS = 402;
const PAYMENT_VERIFICATION_REQUIRED_ERROR = 'payment_verification_required';

/** Stripe intent statuses that mean the diner passed their bank's check (manual capture → `requires_capture`). */
export const VERIFIED_PAYMENT_INTENT_STATUSES: readonly string[] = [
  'requires_capture',
  'succeeded',
];

export interface PaymentVerificationChallenge {
  paymentIntentId: string;
  clientSecret: string;
  stripeAccountId: string;
}

const STRIPE_ACCOUNT_ID_PATTERN = /^acct_[A-Za-z0-9]+$/;
const REDACTED = '[redacted]';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The JSON body off a `WretchError` — its parsed `json`, or its raw `text` when that is JSON. */
function readErrorBody(error: Record<string, unknown>): Record<string, unknown> | null {
  if (isRecord(error.json)) {
    return error.json;
  }
  if (typeof error.text !== 'string') {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(error.text);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The verification challenge carried by a failed basket submit, or `null` when the failure is
 * anything else. Fails closed, like coo-api: a 402 whose challenge is missing or malformed returns
 * `null`, so it takes today's generic submission-error path rather than a challenge that cannot run.
 */
export function getPaymentVerificationChallenge(
  error: unknown,
): PaymentVerificationChallenge | null {
  if (!isRecord(error) || error.status !== PAYMENT_VERIFICATION_REQUIRED_STATUS) {
    return null;
  }
  const body = readErrorBody(error);
  if (body?.error !== PAYMENT_VERIFICATION_REQUIRED_ERROR || !isRecord(body.error_data)) {
    return null;
  }

  const {
    payment_intent_id: paymentIntentId,
    client_secret: clientSecret,
    stripe_account_id: stripeAccountId,
  } = body.error_data;
  if (
    typeof paymentIntentId !== 'string' ||
    !STRIPE_PAYMENT_INTENT_ID_PATTERN.test(paymentIntentId) ||
    typeof clientSecret !== 'string' ||
    !clientSecret ||
    typeof stripeAccountId !== 'string' ||
    !STRIPE_ACCOUNT_ID_PATTERN.test(stripeAccountId)
  ) {
    return null;
  }

  return { paymentIntentId, clientSecret, stripeAccountId };
}

/**
 * The submission's payment entries, tolerating a payload without them. These predicates run at the
 * top of the submit saga's `catch`, where a throw would skip every error route below it.
 */
function paymentEntries(orderData: SubmitBasketData): SubmitBasketData['payment'] {
  return Array.isArray(orderData?.payment) ? orderData.payment : [];
}

/**
 * Whether this submission pays with a tokenized card — the only payment the resume can carry an
 * intent for. coo-api refuses `payment_intent_id` on a `digital_wallet` entry, so a wallet 402 must
 * not start a challenge whose resubmit could only resend the spent wallet token.
 */
export function hasCardTokenPayment(orderData: SubmitBasketData): boolean {
  return paymentEntries(orderData).some((entry) => entry.type === PAYMENT_TYPES.CREDIT_CARD_TOKEN);
}

/** Whether this submission is already the verified resubmit — it never starts a second challenge. */
export function isVerificationResume(orderData: SubmitBasketData): boolean {
  return paymentEntries(orderData).some((entry) => Boolean(entry.payment_intent_id));
}

/**
 * The resubmit for a verified intent: the first leg's payload, unchanged except that the card entry
 * carries `payment_intent_id` and no `card_token` (the token was spent on the first leg; coo-api
 * refuses both together). Nothing else may change — chowly-api fails the resume terminally if the
 * amount differs from the intent — which is why this copies the submitted payload rather than
 * reassembling it from state.
 */
export function buildVerificationResumeOrderData(
  orderData: SubmitBasketData,
  paymentIntentId: string,
): SubmitBasketData {
  return {
    ...orderData,
    payment: orderData.payment.map((entry) => {
      if (entry.type !== PAYMENT_TYPES.CREDIT_CARD_TOKEN) {
        return entry;
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { card_token, save, ...rest } = entry;
      return { ...rest, save: false, payment_intent_id: paymentIntentId };
    }),
  };
}

/** Last four characters of an intent id — enough to correlate in logs without logging it whole. */
export function maskPaymentIntentId(paymentIntentId: string): string {
  return `****${paymentIntentId.slice(-4)}`;
}

const SECRET_PATTERNS: [RegExp, string][] = [
  // The JSON pair in a serialised body, plain and once-escaped (a body stringified inside a string).
  [/("client_secret"\s*:\s*)"[^"]*"/g, `$1"${REDACTED}"`],
  [/(\\"client_secret\\"\s*:\s*)\\"[^"\\]*\\"/g, `$1\\"${REDACTED}\\"`],
  // A bare Stripe client secret anywhere else in a string.
  [/pi_[A-Za-z0-9]+_secret_[A-Za-z0-9]+/g, REDACTED],
];

/**
 * A copy of a value about to be logged with every Stripe `client_secret` removed: `client_secret`
 * keys at any depth, the JSON pair inside a serialised body, and any bare `pi_…_secret_…` token.
 * Everything else is returned as it was.
 */
export function redactVerificationSecret<T>(value: T): T {
  if (typeof value === 'string') {
    return SECRET_PATTERNS.reduce<string>(
      (text, [pattern, replacement]) => text.replace(pattern, replacement),
      value,
    ) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item: unknown) => redactVerificationSecret(item)) as T;
  }
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        key === 'client_secret' ? REDACTED : redactVerificationSecret(item),
      ]),
    ) as T;
  }
  return value;
}
